"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

// Stages roughly mirror the backend optimize pipeline so the widget can
// surface meaningful state even while the agent has navigated to a
// different page. The agent doesn't need to babysit the route screen
// while geocoding or TSP solver runs.
export type RouteJobStage =
  | "queued"
  | "geocoding"   // Census + Nominatim mop-up
  | "optimizing"  // 23-op TSP solver + ORS warm-start
  | "rendering"   // OSRM directions polyline
  | "completing"  // 100% animation tail
  | "completed"
  | "failed";

export interface RouteJob {
  id: string;                     // local job UUID, lives only in this client
  routeId?: string;               // backend route_plan id, set when known
  routeName?: string;             // display label
  totalStops: number;             // approximate stop count
  stage: RouteJobStage;
  message: string;
  startTime: Date;
  endTime?: Date;
  stats?: {
    distanceMeters?: number;
    durationSeconds?: number;
    geocodedCount?: number;
  };
  errorMessage?: string;
}

interface RouteProgressContextType {
  activeRouteJobs: RouteJob[];
  startRouteJob: (id: string, opts: { routeName?: string; totalStops: number }) => void;
  updateRouteJob: (id: string, patch: Partial<Omit<RouteJob, "id" | "startTime">>) => void;
  completeRouteJob: (id: string, opts: { routeId?: string; stats?: RouteJob["stats"] }) => void;
  failRouteJob: (id: string, errorMessage: string) => void;
  dismissRouteJob: (id: string) => void;
  clearAllRouteJobs: () => void;
}

const RouteProgressContext = createContext<RouteProgressContextType | undefined>(undefined);

export const RouteProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeRouteJobs, setActiveRouteJobs] = useState<RouteJob[]>([]);

  // Auto-dismiss completed jobs after 12s so the widget doesn't pile up
  // stale "completed" cards. Failures stay until manually dismissed —
  // the agent should always see what went wrong.
  const dismissTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => {
    for (const job of activeRouteJobs) {
      if (job.stage === "completed" && !dismissTimers.current[job.id]) {
        dismissTimers.current[job.id] = setTimeout(() => {
          setActiveRouteJobs(prev => prev.filter(j => j.id !== job.id));
          delete dismissTimers.current[job.id];
        }, 12000);
      }
    }
    return () => {
      // Component unmount cleanup only — timers for still-active jobs
      // are handled above.
    };
  }, [activeRouteJobs]);

  const startRouteJob = useCallback<RouteProgressContextType["startRouteJob"]>((id, opts) => {
    setActiveRouteJobs(prev => [
      ...prev,
      {
        id,
        routeName: opts.routeName,
        totalStops: opts.totalStops,
        stage: "geocoding",
        message: "Geocoding addresses…",
        startTime: new Date(),
      },
    ]);
  }, []);

  const updateRouteJob = useCallback<RouteProgressContextType["updateRouteJob"]>((id, patch) => {
    setActiveRouteJobs(prev => prev.map(job => job.id === id ? { ...job, ...patch } : job));
  }, []);

  const completeRouteJob = useCallback<RouteProgressContextType["completeRouteJob"]>((id, opts) => {
    setActiveRouteJobs(prev => prev.map(job =>
      job.id === id
        ? {
            ...job,
            stage: "completing",
            message: "Finalizing…",
            routeId: opts.routeId ?? job.routeId,
            stats: opts.stats ?? job.stats,
          }
        : job,
    ));
    // Brief tail so the progress bar fully fills before flipping to the
    // success state — feels less abrupt than snapping to 100% instantly.
    setTimeout(() => {
      setActiveRouteJobs(prev => prev.map(job =>
        job.id === id
          ? {
              ...job,
              stage: "completed" as const,
              message: "Route ready",
              endTime: new Date(),
            }
          : job,
      ));
    }, 600);
  }, []);

  const failRouteJob = useCallback<RouteProgressContextType["failRouteJob"]>((id, errorMessage) => {
    setActiveRouteJobs(prev => prev.map(job =>
      job.id === id
        ? {
            ...job,
            stage: "failed" as const,
            message: errorMessage,
            errorMessage,
            endTime: new Date(),
          }
        : job,
    ));
  }, []);

  const dismissRouteJob = useCallback<RouteProgressContextType["dismissRouteJob"]>((id) => {
    if (dismissTimers.current[id]) {
      clearTimeout(dismissTimers.current[id]);
      delete dismissTimers.current[id];
    }
    setActiveRouteJobs(prev => prev.filter(j => j.id !== id));
  }, []);

  const clearAllRouteJobs = useCallback(() => {
    Object.values(dismissTimers.current).forEach(clearTimeout);
    dismissTimers.current = {};
    setActiveRouteJobs([]);
  }, []);

  return (
    <RouteProgressContext.Provider
      value={{
        activeRouteJobs,
        startRouteJob,
        updateRouteJob,
        completeRouteJob,
        failRouteJob,
        dismissRouteJob,
        clearAllRouteJobs,
      }}
    >
      {children}
    </RouteProgressContext.Provider>
  );
};

export const useRouteProgress = () => {
  const ctx = useContext(RouteProgressContext);
  if (!ctx) throw new Error("useRouteProgress must be used inside RouteProgressProvider");
  return ctx;
};
